import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Team, CreateTeamData, UpdateTeamData } from '../../types/organization.types';
import { useCompanyOptions, useCreateTeam, useUpdateTeam } from '../../hooks/useOrgQueries';
import { LedgerDialog, LedgerField, LedgerInput, LedgerSelect, LedgerSelectOption } from '../ui';
import { cn } from '../../lib/cn';

const validationSchema = yup.object({
  name: yup
    .string()
    .default('')
    .required('Team name is required')
    .test('non-blank', 'Team name is required', (value) => Boolean(value && value.trim())),
  description: yup.string().defined().default(''),
  company: yup.string().default('').required('Company is required'),
  maxDepartments: yup
    .number()
    .typeError('Must be a number')
    .min(1, 'Must be at least 1')
    .nullable()
    .defined()
    .default(5),
  isActive: yup.boolean().defined().default(true),
});

type TeamFormValues = yup.InferType<typeof validationSchema>;

const defaultValues: TeamFormValues = {
  name: '',
  description: '',
  company: '',
  maxDepartments: 5,
  isActive: true,
};

interface TeamFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  team?: Team | null;
}

export const TeamFormDialog: React.FC<TeamFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  team,
}) => {
  const [error, setError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const companiesQuery = useCompanyOptions(open && !team);
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const saving = createTeam.isPending || updateTeam.isPending;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TeamFormValues>({ resolver: yupResolver(validationSchema), defaultValues, mode: 'onTouched' });

  // Reseed whenever the dialog opens or its subject changes so a stale edit
  // subject or error strip never leaks into the next open.
  useEffect(() => {
    setError(null);
    if (team) {
      reset({
        name: team.name,
        description: team.description || '',
        company: team.company,
        maxDepartments: team.maxDepartments,
        isActive: team.isActive,
      });
    } else {
      reset(defaultValues);
    }
  }, [team, open, reset]);

  const companyOptions: LedgerSelectOption[] = [
    { value: '', label: 'Select a company' },
    ...(companiesQuery.data ?? []).map((company) => ({
      value: company.id,
      label: company.name,
    })),
  ];

  // The disabled edit-mode query stays "pending" forever, so only create mode
  // may hold the submit button hostage on it.
  const optionsPending = !team && companiesQuery.isPending;

  const handleClose = () => {
    reset(defaultValues);
    setError(null);
    onClose();
  };

  const onSubmit = async (values: TeamFormValues) => {
    setError(null);
    try {
      if (team) {
        // Updates never move a team between companies, so no company here
        const updateData: UpdateTeamData = {
          name: values.name,
          description: values.description,
          maxDepartments: values.maxDepartments ?? undefined,
          isActive: values.isActive,
        };
        await updateTeam.mutateAsync({ id: team.id, data: updateData });
      } else {
        const createData: CreateTeamData = {
          name: values.name,
          description: values.description,
          company: values.company,
          maxDepartments: values.maxDepartments ?? undefined,
        };
        await createTeam.mutateAsync(createData);
      }

      onSuccess();
      handleClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail || 'Failed to save team');
    }
  };

  const nameField = register('name');

  return (
    <LedgerDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title={team ? 'Edit Team' : 'Create New Team'}
      size="md"
      preventClose={saving}
      initialFocus={nameInputRef}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="team-form"
            disabled={saving || isSubmitting || optionsPending}
            className="cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:cursor-default disabled:opacity-60"
          >
            {saving ? 'Saving…' : team ? 'Update' : 'Create'}
          </button>
        </>
      }
    >
      <form id="team-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {(error || companiesQuery.isError) && (
          <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
            {error ?? 'Failed to load companies'}
          </p>
        )}

        <LedgerField label="Team Name" htmlFor="team-name" error={errors.name?.message}>
          <LedgerInput
            id="team-name"
            required
            invalid={!!errors.name}
            {...nameField}
            ref={(element) => {
              nameField.ref(element);
              nameInputRef.current = element;
            }}
          />
        </LedgerField>

        <LedgerField label="Description" htmlFor="team-description">
          <textarea
            id="team-description"
            rows={3}
            className="w-full border border-rule-faint bg-transparent p-2 text-scale-1 text-ink outline-none focus:border-accent"
            {...register('description')}
          />
        </LedgerField>

        {!team && (
          <LedgerField label="Company" error={errors.company?.message}>
            <Controller
              name="company"
              control={control}
              render={({ field }) => (
                <LedgerSelect
                  ariaLabel="Company"
                  value={field.value}
                  options={companyOptions}
                  onValueChange={field.onChange}
                  disabled={companiesQuery.isPending}
                />
              )}
            />
          </LedgerField>
        )}

        <div className="cap-label mt-2 border-b border-ink pb-0.5">Limits</div>
        <div className="pt-3">
          <LedgerField
            label="Maximum Departments"
            htmlFor="team-max-departments"
            error={errors.maxDepartments?.message}
          >
            <LedgerInput
              id="team-max-departments"
              type="number"
              invalid={!!errors.maxDepartments}
              {...register('maxDepartments', { valueAsNumber: true })}
            />
          </LedgerField>
        </div>

        {team && (
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <button
                type="button"
                role="switch"
                aria-checked={field.value}
                aria-label="Active"
                onClick={() => field.onChange(!field.value)}
                className="mt-1 flex cursor-pointer items-center gap-2"
              >
                <span aria-hidden className={cn('h-3 w-3 border border-ink', field.value && 'bg-ink')} />
                <span className="cap-label">{field.value ? 'Active' : 'Not active'}</span>
              </button>
            )}
          />
        )}
      </form>
    </LedgerDialog>
  );
};
